import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import type { TerminalProfileEntity } from "src/app/features/TerminalProfile/types/TerminalProfileEntity";
import { deepClone } from "../../../core/utils/utilFunctions";

const UPDATE_FIELDS = [
    "terminalType",
    "cardEntryMode",
    "serialProtocol",
    "posIpAddress",
    "posPort",
    "deviceApiToken",
    "abortNeedsRequestId",
    "cloudFlowEnabled",
    "enabled",
    "eventTimeout",
    "printLineLength",
    "opiTransport",
    "ch0Port",
    "ch1Port",
    "port",
    "uri",
];

const UPDATE_NESTED_FIELD_PREFIXES = [
    "merchantReferenceNumber1.",
    "merchantReferenceNumber2.",
    "tipHandling.",
    "surchargeAmount.",
    "taxAmount.",
];

// Terminal fields which can be marked as read-only in the terminal profile, with the read-only flag key of the (flattened) profile
const READ_ONLY_FIELDS: Record<string, string> = {
    serialProtocol: "serialProtocolRO",
    posIpAddress: "posIpAddressRO",
    posPort: "posPortRO",
    deviceApiToken: "deviceApiTokenRO",
    abortNeedsRequestId: "abortNeedsRequestIdRO",
    cloudFlowEnabled: "cloudFlowEnabledRO",
    ch0Port: "ch0PortRO",
    ch1Port: "ch1PortRO",
    port: "portRO",
    uri: "uriRO",
};

const readOnlyFlagKey = (fieldName: string) => `${fieldName}CheckReadOnly`;

export const updateWatchedFieldsUtils = (profile: TerminalProfileEntity, formData: TerminalEntity): TerminalEntity => {
    const newFormData = deepClone(formData);
    const profileValues = profile as unknown as Record<string, unknown>;

    // Set field values from terminal profile
    UPDATE_FIELDS.forEach((fieldName: string) => {
        newFormData[fieldName] = profile[fieldName as keyof TerminalProfileEntity];
    });

    // Copy nested objects – these are already flattened (dot-notation keys) on the profile entity
    Object.keys(profile).forEach((key) => {
        if (UPDATE_NESTED_FIELD_PREFIXES.some((prefix) => key.startsWith(prefix))) {
            newFormData[key] = profile[key as keyof TerminalProfileEntity];
        }
    });

    // Set readOnly visibility fields from terminal profile
    Object.entries(READ_ONLY_FIELDS).forEach(([fieldName, profileReadOnlyKey]) => {
        newFormData[readOnlyFlagKey(fieldName)] = profileValues[profileReadOnlyKey];
    });

    // Handle dynamic properties
    const dynamicPropertiesList = profile?.dynamicProperties?.dynamicPropertyValues || [];
    newFormData.dynamicProperties = {
        dynamicPropertyGroupValue: profile?.dynamicProperties?.dynamicPropertyGroupValue,
        dynamicPropertyValues: dynamicPropertiesList.map(({ id: _id, ...rest }) => {
            return {
                id: _id,
                ...rest,
            };
        }),
    };

    return newFormData;
};

// Form changes do not carry the *CheckReadOnly flags - keeps the ones set from the terminal profile.
export const keepTerminalProfileReadOnlyFlagsUtils = (newData: TerminalEntity, currentData: TerminalEntity | undefined): TerminalEntity => {
    const result: Record<string, unknown> = { ...newData };
    Object.keys(READ_ONLY_FIELDS).forEach((fieldName) => {
        const key = readOnlyFlagKey(fieldName);
        result[key] = currentData?.[key as keyof TerminalEntity];
    });
    return result as TerminalEntity;
};
